import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useFavorites } from '../context/FavoritesContext';
import { HeartIcon as OutlineHeartIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { HeartIcon as SolidHeartIcon } from '@heroicons/react/24/solid';
import ProductCardSkeleton from '../components/ProductCardSkeleton';
import ProductCard from '../components/ProductCard'; // Ana ProductCard bileşenini import et
import api from '../utils/api';
import SEOHead from '../components/SEOHead';
import { trackViewItemList } from '../utils/analytics';

const categories = ['T-shirt', 'Gömlek', 'Pantolon', 'Etek', 'Elbise', 'Şort', 'Mont', 'Ayakkabı', 'Aksesuar'];
const genders = ['Kız Çocuk', 'Erkek Çocuk', 'Unisex'];

// Sayfa için SSS verisi
const faqData = [
  {
    question: "Çocuk kıyafeti alırken nelere dikkat edilmeli?",
    answer: "Çocuk kıyafeti seçerken en önemli faktör kumaşın kalitesidir. %100 pamuklu, nefes alan ve terletmeyen kumaşlar tercih edilmelidir. Ayrıca, çocuğun rahat hareket etmesini sağlayacak esnek ve bol kesimler önemlidir. Dikişlerin sağlamlığı ve kullanılan boyaların zararlı kimyasallar içermemesi de göz önünde bulundurulmalıdır."
  },
  {
    question: "Çocuklar için doğru kıyafet bedeni nasıl seçilir?",
    answer: "Çocuklar hızla büyüdüğü için beden seçimi zorlayıcı olabilir. En doğru yöntem, çocuğunuzun boy ve kilo ölçülerini alıp markanın beden tablosuyla karşılaştırmaktır. Genel bir kural olarak, mevcut bedeninden bir beden büyük almak, kıyafetin daha uzun süre kullanılmasını sağlayabilir."
  },
  {
    question: "Minela Kids ürünleri hangi yaş aralığına hitap ediyor?",
    answer: "Minela Kids olarak yenidoğan döneminden başlayarak 12 yaşa kadar geniş bir yaş aralığı için ürünler sunmaktayız. Koleksiyonlarımızda her yaş grubunun ihtiyaçlarına ve konforuna uygun tasarımlar bulabilirsiniz."
  }
];

// FAQPage Schema.org structured data
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [sortOption, setSortOption] = useState('newest');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [pageTitle, setPageTitle] = useState('Ürünler');
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlCategories = params.getAll('category');
    const urlGenders = params.getAll('gender');
    const urlSort = params.get('sort') || 'newest';
    const urlSearch = params.get('search');

    setSelectedCategories(urlCategories);
    setSelectedGenders(urlGenders);
    setSortOption(urlSort);

    if (urlSearch) {
      setPageTitle(`'${urlSearch}' için arama sonuçları`);
    } else if (urlCategories.length > 0) {
      setPageTitle(urlCategories.join(', '));
    } else if (urlGenders.length > 0) {
      setPageTitle(urlGenders.join(', '));
    } else {
      setPageTitle('Tüm Ürünler');
    }
  }, [location.search]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams(location.search);
      const queryString = params.toString();
      const urlSearch = params.get('search');
      
      try {
        // 2. DEĞİŞİKLİK: fetch yerine api() kullanıldı.
        const data = await api(`/api/products?${queryString}`);
        const productList = data?.products || data || [];
        setProducts(productList);
        
        // GA4: Ürün listesi görüntüleme
        if (productList && productList.length > 0) {
          let listName = 'Ürün Listesi';
          if (urlSearch) {
            listName = `Arama: ${urlSearch}`;
          } else if (selectedCategories.length > 0) {
            listName = `Kategori: ${selectedCategories.join(', ')}`;
          } else if (selectedGenders.length > 0) {
            listName = `Cinsiyet: ${selectedGenders.join(', ')}`;
          }
          
          trackViewItemList(productList, listName);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [location.search]);

  const handleFilterChange = () => {
    const params = new URLSearchParams();
    if (sortOption !== 'newest') params.set('sort', sortOption);
    selectedCategories.forEach(cat => params.append('category', cat));
    selectedGenders.forEach(gen => params.append('gender', gen));
    const currentSearch = new URLSearchParams(location.search).get('search');
    if (currentSearch) {
        params.set('search', currentSearch);
    }
    navigate(`/products?${params.toString()}`);
  };
  
  const handleCategoryChange = (category) => {
    const newSelection = selectedCategories.includes(category) 
      ? selectedCategories.filter(c => c !== category)
      : [...selectedCategories, category];
    setSelectedCategories(newSelection);
  };

  const handleGenderChange = (gender) => {
    const newSelection = selectedGenders.includes(gender)
      ? selectedGenders.filter(g => g !== gender)
      : [...selectedGenders, gender];
    setSelectedGenders(newSelection);
  };
  
  useEffect(() => {
    handleFilterChange();
    // eslint-disable-next-line
  }, [sortOption, selectedCategories, selectedGenders]);

  return (
    <>
      <SEOHead
        title={`${pageTitle} - Çocuk Giyim Koleksiyonu | Minela Kids`}
        description={`Minela Kids'in ${pageTitle.toLowerCase()} koleksiyonunu keşfedin. En kaliteli ve şık çocuk giyim ürünleri burada. Güvenli alışveriş ve hızlı kargo imkanı.`}
        structuredData={faqSchema}
      />
      <div className="p-2 sm:p-4 md:p-6">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-text-dark text-center mb-6 sm:mb-8 md:mb-10 leading-tight px-2">
          {pageTitle}
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 items-start">
          <aside className="lg:col-span-1 bg-white p-4 sm:p-6 rounded-lg shadow-md h-fit lg:sticky lg:top-24">
            <div className="mb-4 sm:mb-6">
              <h3 className="font-bold text-base sm:text-lg mb-2">Sırala</h3>
              <select 
                value={sortOption} 
                onChange={(e) => setSortOption(e.target.value)} 
                className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-md focus:ring-brand-green focus:border-brand-green bg-white"
              >
                <option value="newest">En Yeniler</option>
                <option value="price_asc">Fiyata Göre (Artan)</option>
                <option value="price_desc">Fiyata Göre (Azalan)</option>
              </select>
            </div>
            <div className="mb-4 sm:mb-6">
              <h3 className="font-bold text-base sm:text-lg mb-2">Kategori</h3>
              <div className="space-y-2">
                {categories.map(cat => (
                  <label key={cat} className="flex items-center space-x-2 cursor-pointer text-gray-700 hover:text-brand-green text-sm sm:text-base">
                    <input 
                      type="checkbox" 
                      checked={selectedCategories.includes(cat)} 
                      onChange={() => handleCategoryChange(cat)} 
                      className="h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green flex-shrink-0"
                    />
                    <span className="break-words">{cat}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-bold text-base sm:text-lg mb-2">Cinsiyet</h3>
              <div className="space-y-2">
                {genders.map(gen => (
                  <label key={gen} className="flex items-center space-x-2 cursor-pointer text-gray-700 hover:text-brand-green text-sm sm:text-base">
                    <input 
                      type="checkbox" 
                      checked={selectedGenders.includes(gen)} 
                      onChange={() => handleGenderChange(gen)} 
                      className="h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green flex-shrink-0"
                    />
                    <span className="break-words">{gen}</span>
                  </label>
                ))}
              </div>
            </div>
          </aside>
          <main className="lg:col-span-3">
            {error ? (
              <div className="text-center text-red-600 p-4 sm:p-6 md:p-8 bg-red-50 rounded-lg text-sm sm:text-base">Hata: {error}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)
                ) : products.length === 0 ? (
                  <div className="text-center text-gray-500 p-4 sm:p-6 md:p-8 col-span-full text-sm sm:text-base">Seçili filtrelere uygun ürün bulunamadı.</div>
                ) : (
                  products.map((product) => <ProductCard key={product.id} product={product} />)
                )}
              </div>
            )}
          </main>
        </div>

        {/* Featured Snippet Hedefli SSS Bölümü */}
        <div className="mt-8 sm:mt-12 md:mt-16 max-w-4xl mx-auto px-2">
          <div className="bg-gray-50 p-4 sm:p-6 md:p-8 rounded-lg shadow-inner">
            <h2 className="text-xl sm:text-2xl font-bold text-center text-brand-green mb-4 sm:mb-6">
              Çocuk Giyimi Hakkında Merak Edilenler
            </h2>
            <div className="space-y-4 sm:space-y-6">
              {faqData.map((item, index) => (
                <div key={index} className="border-b pb-4 last:border-b-0">
                  <h3 className="text-base sm:text-lg font-semibold text-text-dark mb-2">{item.question}</h3>
                  <p className="text-sm sm:text-base text-text-light leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ProductCard bileşeni artık components/ProductCard.js'den import ediliyor

export default ProductsPage;
